import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  checkCampusUrl,
  getCampus,
  getCampuses,
  createCampus,
  updateCampus,
} from '../api/campuses.api';
import {
  ICampus,
  IGetCampus,
  ICampusItem,
  IGetCampuses,
} from '../models/campuses.model';

interface ICampusState {
  campus?: ICampus;
  campuses?: ICampusItem[];
  isLoading: boolean;
  error?: AxiosError | string;
}

const initialState: ICampusState = {
  campus: undefined,
  campuses: [],
  isLoading: false,
};

const campusSlicer = createSlice({
  name: 'campus',
  initialState,
  reducers: {
    getCampusStart: (state: ICampusState) => {
      state.campus = undefined;
      state.isLoading = true;
      state.error = undefined;
    },
    getCampusSuccess: (
      state: ICampusState,
      action: PayloadAction<IGetCampus>,
    ) => {
      state.campus = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getCampusFailure: (
      state: ICampusState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.campus = undefined;
      state.isLoading = false;
      state.error = action.payload;
    },
    getCampusesStart: (state: ICampusState) => {
      state.campuses = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getCampusesSuccess: (
      state: ICampusState,
      action: PayloadAction<IGetCampuses>,
    ) => {
      state.campuses = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getCampusesFailure: (
      state: ICampusState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.campuses = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    requestStart: (state: ICampusState) => {
      state.isLoading = true;
      state.error = undefined;
    },
    requestFailure: (
      state: ICampusState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    createSuccess: (state: ICampusState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    updateSuccess: (state: ICampusState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    checkSuccess: (state: ICampusState) => {
      state.isLoading = false;
      state.error = undefined;
    },
  },
});

export const {
  getCampusStart,
  getCampusSuccess,
  getCampusFailure,
  getCampusesStart,
  getCampusesSuccess,
  getCampusesFailure,
  requestStart,
  requestFailure,
  createSuccess,
  updateSuccess,
  checkSuccess,
} = campusSlicer.actions;

export default campusSlicer.reducer;

export const doCheckCampusUrl =
  (uri: string): any =>
  async (dispatch: any) => {
    try {
      const response = await checkCampusUrl(uri);
      dispatch(checkSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetCampus =
  (id: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getCampusStart());
      const response = await getCampus(id);
      dispatch(getCampusSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getCampusFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetCampuses = (): any => async (dispatch: any) => {
  try {
    dispatch(getCampusesStart());
    const response = await getCampuses();
    dispatch(getCampusesSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getCampusesFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doCreateCampus =
  (data: any): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createCampus(data);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateCampus =
  (id: string, data: any): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateCampus(id, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };
